import type { CSSProperties } from 'react';

import type { Prettify } from '@infinex-private/ts-utils/types';

import type { BreakpointRange, ResponsiveProp } from './responsive';

export type Responsive<T> = T | ResponsiveProp<T>;
export type PointerType = 'coarse' | 'fine';
export type PointerProp<T> = Record<PointerType, T>;

/**
 * Includes intrinsic layout props for managing the elements position within a
 * layout container, like `alignSelf` and `gridColStart` etc. as well as margin
 * props.
 */
export type BaseStyleProps = Prettify<MarginProps & SelfLayoutProps>;

/**
 * Includes box-model layout props for managing the element's dimensions, like
 * `width` and `height`, their min/max companions, as well as padding and
 * "bleed" (negative margin) props.
 */
export type BoxStyleProps = Prettify<
  DimensionProps & PaddingProps & BleedProps
>;

/** A collection of style-related props common to most components. */
export type UnsafeStyleProps = {
  /** A class name, or space-separated classes, to apply to the element. */
  className?: string | undefined;
  /** A style object to apply to the element. */
  style?: CSSProperties | undefined;
};

// Dimension
// -----------------------------------------------------------------------------

// Essentially just `string` but with a few common values for IDE autocomplete.
export type AspectRatio = '1/1' | '4/3' | '16/9' | ({} & string);

// TODO: where should this live?
export const DIMENSION_CONTAINER = {
  small: '420px',
  medium: '640px',
  large: '960px',
} as const;

export type Dimension =
  | DimensionPixel
  | DimensionFraction
  | DimensionKeyword
  | DimensionLength
  | DimensionContainer;

export type DimensionLength = '100%' | '100vh' | '100vw';
// prettier-ignore
type DimensionNumber = | 0 | 16 | 20 | 24 | 28 | 32 | 36 | 40 | 44 | 48 | 56 | 64 | 80 | 96 | 112 | 128 | 144 | 160 | 176 | 192 | 208 | 224 | 240 | 256 | 288 | 320 | 384;
export type DimensionPixel = `${DimensionNumber}px`;
// prettier-ignore
export type DimensionFraction = | '1/2' | '1/3' | '2/3' | '1/4' | '2/4' | '3/4' | '1/5' | '2/5' | '3/5' | '4/5';
// prettier-ignore
export type DimensionKeyword = | 'auto' | 'fit-content' | 'max-content' | 'min-content';
export type DimensionContainer = keyof typeof DIMENSION_CONTAINER;

// Spacing
// -----------------------------------------------------------------------------

// prettier-ignore
type SpacingNumber = | 0 | 2 | 4 | 6 | 8 | 12 | 16 | 20 | 24 | 32 | 40 | 48 | 56 | 64;
export type SpacingPixel = `${SpacingNumber}px`;
export type Spacing = SpacingPixel;
export type Gap = Spacing;
export type Bleed = Spacing; // negative margin
export type Margin = Spacing | 'auto' | 'full';
export type Padding = Spacing;

// Self layout
// -----------------------------------------------------------------------------

export type AlignPhysicalY = 'top' | 'center' | 'bottom';
export type AlignPhysicalX = 'left' | 'center' | 'right';
export type AlignLogical = 'start' | 'center' | 'end';
// prettier-ignore
export type ContentDistribution = | 'space-around' | 'space-between' | 'space-evenly' | 'stretch';
export type GridNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
export type ColValue = `${GridNumber}`;
export type RowValue = `${GridNumber}`;

// Prop types
// -----------------------------------------------------------------------------

export type MarginProps = {
  /** The margin for all four sides of the element. */
  margin?: Responsive<Margin>;
  /** The margin for both the top and bottom sides of the element. */
  marginY?: Responsive<Margin>;
  /** The margin for both the left and right sides of the element. */
  marginX?: Responsive<Margin>;
  /** The margin for the left side of the element. */
  marginLeft?: Responsive<Margin>;
  /** The margin for the right side of the element. */
  marginRight?: Responsive<Margin>;
  /** The margin for the top side of the element. */
  marginTop?: Responsive<Margin>;
  /** The margin for the bottom side of the element. */
  marginBottom?: Responsive<Margin>;
};

export type SelfLayoutProps = {
  /** When used in a flex layout, specifies how the element will grow or shrink to fit the space available. */
  flex?: Responsive<boolean | 'auto' | 'initial' | 'none'>;
  /** When used in a flex layout, specifies how the element will grow to fit the space available. */
  flexGrow?: Responsive<boolean>;
  /** When used in a flex layout, specifies how the element will shrink to fit the space available. */
  flexShrink?: Responsive<boolean>;
  /** When used in a flex layout, specifies the initial main size of the element. */
  flexBasis?: Responsive<DimensionPixel>;
  /** Overrides the `alignItems` property of a flex or grid container. */
  alignSelf?: Responsive<AlignLogical | 'stretch' | 'baseline'>;
  /** Specifies how the element is justified inside a flex or grid container. */
  justifySelf?: Responsive<AlignLogical>;
  /** The layout order for the element within a flex or grid container. */
  order?: Responsive<'first' | 'last'>;
  /** Responsively hide the element. */
  isHidden?: BreakpointRange;
  /** When used in a grid layout, specifies the number of columns to span. */
  gridColSpan?: Responsive<ColValue>;
  /** When used in a grid layout, specifies the starting column. */
  gridColStart?: Responsive<ColValue>;
  /** When used in a grid layout, specifies the ending column. */
  gridColEnd?: Responsive<ColValue>;
  /** When used in a grid layout, specifies the number of rows to span. */
  gridRowSpan?: Responsive<RowValue>;
  /** When used in a grid layout, specifies the starting row. */
  gridRowStart?: Responsive<RowValue>;
  /** When used in a grid layout, specifies the ending row. */
  gridRowEnd?: Responsive<RowValue>;
};

export type DimensionProps = {
  /** The aspect ratio of the element. */
  aspectRatio?: Responsive<AspectRatio>;
  /** The width of the element. */
  width?: Responsive<Dimension>;
  /** The height of the element. */
  height?: Responsive<Dimension>;
  /** The minimum width of the element. */
  minWidth?: Responsive<Dimension | 'none'>;
  /** The minimum height of the element. */
  minHeight?: Responsive<Dimension | 'none'>;
  /** The maximum width of the element. */
  maxWidth?: Responsive<Dimension | 'none'>;
  /** The maximum height of the element. */
  maxHeight?: Responsive<Dimension | 'none'>;
};

export type PaddingProps = {
  /** The padding for all four sides of the element. */
  padding?: Responsive<Padding>;
  /** The padding for both the top and bottom sides of the element. */
  paddingY?: Responsive<Padding>;
  /** The padding for both the left and right sides of the element. */
  paddingX?: Responsive<Padding>;
  /** The padding for the left side of the element. */
  paddingLeft?: Responsive<Padding>;
  /** The padding for the right side of the element. */
  paddingRight?: Responsive<Padding>;
  /** The padding for the top side of the element. */
  paddingTop?: Responsive<Padding>;
  /** The padding for the bottom side of the element. */
  paddingBottom?: Responsive<Padding>;
};

export type BleedProps = {
  /** The negative margin for all four sides of the element. */
  bleed?: Responsive<Bleed>;
  /** The negative margin for both the top and bottom sides of the element. */
  bleedY?: Responsive<Bleed>;
  /** The negative margin for both the left and right sides of the element. */
  bleedX?: Responsive<Bleed>;
  /** The negative margin for the left side of the element. */
  bleedLeft?: Responsive<Bleed>;
  /** The negative margin for the right side of the element. */
  bleedRight?: Responsive<Bleed>;
  /** The negative margin for the top side of the element. */
  bleedTop?: Responsive<Bleed>;
  /** The negative margin for the bottom side of the element. */
  bleedBottom?: Responsive<Bleed>;
};
